import type { CustomStatuses, UserResponse } from "@services/user/types";
import { configureUserRolesParticularities, setUserType } from "@utils/user-permissions";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type AuthStore = {
  user: UserResponse | null;
  userId: string | null;
  securityClearanceRequestID: string | null;
  securityClearanceRequestStatus: string | null;
  // SC id/status from login - not updated when previewing another clearance
  bootstrapSecurityClearanceId: string | null;
  bootstrapSecurityClearanceStatus: string | null;
  securityClearanceRenewID: string | null; // ID used for renew flow
  redirectPath: string;
  customSecurityClearancePath: string | null;
  requestSCLabelKey: string;
  requestGatepassLabelKey: string;
  registerVesselLabelKey: string;
  token: string | null;
  preferences: {
    language: string;
  };
  isPassportNumberSet: boolean;
  userAccessPermissions: string[];
  userActionPermissions: string[];
  userCustomStatuses: CustomStatuses | null;
  isMarineDepartmentUser: boolean;
  isOperationsUser: boolean;
  isShippingAgentUser: boolean;
  isHSEUser: boolean;
  userType?: string;
  setUser: (
    user: UserResponse,
    securityClearanceRequestID: string | null,
    securityClearanceRequestStatus: string | null,
    opts?: { updateBootstrap?: boolean },
  ) => void;
  setLanguage: (language: string) => void;
  clearUser: () => void;
  setAuth: (token: string, userId: string) => void;
  clearAuth: () => void;
  setSecurityClearanceRequestID: (securityClearanceRequestID: string | null) => void;
  setSecurityClearanceRequestStatus: (securityClearanceRequestStatus: string | null) => void;
  setSecurityClearanceRenewID: (securityClearanceRenewID: string | null) => void;
  setIsPassportNumberSet: (isPassportNumberSet: boolean) => void;
  movementServiceResubmit: boolean | null;
  setMovementServiceResubmit: (value: boolean | null) => void;
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      userId: null,
      token: null,
      preferences: { language: "en" },
      isPassportNumberSet: false,
      securityClearanceRequestID: null,
      securityClearanceRequestStatus: null,
      bootstrapSecurityClearanceId: null,
      bootstrapSecurityClearanceStatus: null,
      securityClearanceRenewID: null,
      customSecurityClearancePath: null,
      userAccessPermissions: [],
      userActionPermissions: [],
      redirectPath: "",
      requestSCLabelKey: "",
      requestGatepassLabelKey: "",
      registerVesselLabelKey: "",
      userCustomStatuses: null,
      isMarineDepartmentUser: false,
      isOperationsUser: false,
      isShippingAgentUser: false,
      isHSEUser: false,
      movementServiceResubmit: null,

      setUser: (
        user,
        securityClearanceRequestID = null,
        securityClearanceRequestStatus = null,
        opts?: { updateBootstrap?: boolean },
      ) => {
        // If a different user logs in, clear cross-user artifacts
        const prevUserId = get().userId;
        const isDifferentUser = !!prevUserId && prevUserId !== user.id;
        if (isDifferentUser) {
          set({
            securityClearanceRequestID: null,
            securityClearanceRequestStatus: null,
            bootstrapSecurityClearanceId: null,
            bootstrapSecurityClearanceStatus: null,
            securityClearanceRenewID: null,
            // optional: also clear labels/redirects if you want a clean slate
            redirectPath: "",
            customSecurityClearancePath: null,
            requestSCLabelKey: "",
            requestGatepassLabelKey: "",
            registerVesselLabelKey: "",
            userCustomStatuses: null,
            userAccessPermissions: [],
            userActionPermissions: [],
            isMarineDepartmentUser: false,
            isOperationsUser: false,
            isShippingAgentUser: false,
            isHSEUser: false,
          });
        }
        const operationsLocations = user.terminalId === "OPS" || user.terminalId === "DIBOPS";
        const isOperations = user.roles.includes("terminal") && operationsLocations;
        const {
          bootstrapSecurityClearanceId: storedBootstrapSecurityClearanceId,
          bootstrapSecurityClearanceStatus: storedBootstrapSecurityClearanceStatus,
        } = get();
        const bootstrapSecurityClearanceId = opts?.updateBootstrap
          ? securityClearanceRequestID
          : storedBootstrapSecurityClearanceId;
        const bootstrapSecurityClearanceStatus = opts?.updateBootstrap
          ? securityClearanceRequestStatus
          : storedBootstrapSecurityClearanceStatus;
        const hasBootstrapSc =
          bootstrapSecurityClearanceId != null &&
          String(bootstrapSecurityClearanceId).trim().length > 0;
        const individualLinkScID = hasBootstrapSc ? bootstrapSecurityClearanceId : undefined;
        const individualLinkScStatus = hasBootstrapSc
          ? (bootstrapSecurityClearanceStatus ?? null)
          : undefined;

        const configMap = configureUserRolesParticularities(
          user.permissions,
          securityClearanceRequestID,
          securityClearanceRequestStatus,
          isOperations,
          individualLinkScID,
          individualLinkScStatus,
        );

        // merge all valid role configs
        const roleConfigs = user.roles
          .map((r) => configMap[r as keyof typeof configMap])
          .filter(
            (cfg): cfg is Exclude<typeof cfg, "" | null | undefined> =>
              !!cfg && typeof cfg === "object",
          );

        const mergedAccess = [
          ...new Set(roleConfigs.flatMap((cfg) => cfg.accessPermissionsList ?? [])),
        ];
        const mergedAction = [
          ...new Set(roleConfigs.flatMap((cfg) => cfg.actionPermissionsList ?? [])),
        ];
        // Derive user type flags from all merged role configs, not just the first one
        const isOperationsUser = roleConfigs.some((cfg) => cfg.isOperationsUser);
        const isMarineDepartmentUser = roleConfigs.some((cfg) => cfg.isMarineDepartmentUser);
        const isShippingAgentUser = roleConfigs.some((cfg) => cfg.isShippingAgentUser);
        const isHSEUser = roleConfigs.some((cfg) => cfg.isHSEUser);

        set({
          user,
          userId: user.id,
          isOperationsUser,
          isMarineDepartmentUser,
          isShippingAgentUser,
          isHSEUser,
          userType: setUserType({
            isOperationsUser,
            isMarineDepartmentUser,
            isShippingAgentUser,
            isHSEUser,
          }),

          // IMPORTANT: only keep SC IDs that you explicitly pass for THIS user
          securityClearanceRequestID,
          securityClearanceRequestStatus,
          ...(opts?.updateBootstrap
            ? {
                bootstrapSecurityClearanceId: securityClearanceRequestID,
                bootstrapSecurityClearanceStatus: securityClearanceRequestStatus,
              }
            : {}),

          // take labels/redirects from the first valid role (fallbacks keep state stable)
          redirectPath: roleConfigs[0]?.redirectPath ?? "",
          customSecurityClearancePath: roleConfigs[0]?.customSecurityClearancePath ?? null,
          requestSCLabelKey: roleConfigs[0]?.requestSCLabelKey ?? "",
          requestGatepassLabelKey: roleConfigs[0]?.requestGatepassLabelKey ?? "",
          registerVesselLabelKey: roleConfigs[0]?.registerVesselLabelKey ?? "",
          userCustomStatuses: roleConfigs[0]?.customStatuses ?? null,

          userAccessPermissions: mergedAccess,
          userActionPermissions: mergedAction,
        });
      },

      clearUser: () =>
        set(() => ({
          user: null,
          // leave userId, SC IDs, permissions, etc. untouched
        })),

      setLanguage: (language) =>
        set((state) => ({ preferences: { ...state.preferences, language } })),

      setAuth: (token, userId) => set({ token, userId }),
      clearAuth: () =>
        set({
          token: null,
          userId: null,
          user: null,
          securityClearanceRequestID: null,
          securityClearanceRequestStatus: null,
          bootstrapSecurityClearanceId: null,
          bootstrapSecurityClearanceStatus: null,
          securityClearanceRenewID: null,
          userAccessPermissions: [],
          userActionPermissions: [],
          customSecurityClearancePath: null,
          redirectPath: "",
          requestSCLabelKey: "",
          requestGatepassLabelKey: "",
          registerVesselLabelKey: "",
          userCustomStatuses: null,
          isMarineDepartmentUser: false,
          isOperationsUser: false,
          isShippingAgentUser: false,
          isHSEUser: false,
        }),

      setSecurityClearanceRequestID: (securityClearanceRequestID) =>
        set({ securityClearanceRequestID }),
      setSecurityClearanceRequestStatus: (securityClearanceRequestStatus) =>
        set({ securityClearanceRequestStatus }),
      setSecurityClearanceRenewID: (securityClearanceRenewID) => set({ securityClearanceRenewID }),
      setIsPassportNumberSet: (isPassportNumberSet) => set({ isPassportNumberSet }),
      setMovementServiceResubmit: (movementServiceResubmit) => set({ movementServiceResubmit }),
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
        userId: state.user?.id ?? state.userId,
        isMarineDepartmentUser: state.isMarineDepartmentUser,
        isOperationsUser: state.isOperationsUser,
        isShippingAgentUser: state.isShippingAgentUser,
        isHSEUser: state.isHSEUser,
        userType: state.userType,
        // securityClearanceRequestID: state.securityClearanceRequestID,
        // securityClearanceRequestStatus: state.securityClearanceRequestStatus,
        // securityClearanceRenewID: state.securityClearanceRenewID,
        redirectPath: state.redirectPath,
        customSecurityClearancePath: state.customSecurityClearancePath,
        requestSCLabelKey: state.requestSCLabelKey,
        requestGatepassLabelKey: state.requestGatepassLabelKey,
        registerVesselLabelKey: state.registerVesselLabelKey,
        token: state.token,
        preferences: state.preferences,
        isPassportNumberSet: state.isPassportNumberSet,
        userAccessPermissions: state.userAccessPermissions,
        userActionPermissions: state.userActionPermissions,
        userCustomStatuses: state.userCustomStatuses,
      }),
    },
  ),
);
